import { useEffect, useRef } from "react";
import Footer from "../components/Footer";
import Navbar from "../components/Navbar";

const SECTION_LINKS = [
  { id: "overview", label: "Overview" },
  { id: "mechanics", label: "Mechanics" },
  { id: "flow", label: "How It Works" },
  { id: "use-cases", label: "Use Cases" },
];

const TradePromo = () => {
  const overviewRef = useRef(null);
  const mechanicsRef = useRef(null);
  const flowRef = useRef(null);
  const useCasesRef = useRef(null);
  const ctaRef = useRef(null);

  const sectionRefs = {
    overview: overviewRef,
    mechanics: mechanicsRef,
    flow: flowRef,
    "use-cases": useCasesRef,
    cta: ctaRef,
  };

  const scrollToSection = (sectionId) => {
    sectionRefs[sectionId]?.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("in");
          }
        });
      },
      { threshold: 0.12 },
    );

    const elements = document.querySelectorAll(".reveal");
    elements.forEach((el) => io.observe(el));

    return () => io.disconnect();
  }, []);
  return (
    <>
      <Navbar sectionLinks={SECTION_LINKS} onSectionSelect={scrollToSection} />

      <main id="top">
        <section className="wrap hero-display section-loose reveal">
          <div className="hero-copy">
            <div className="eyebrow">Trade Promotions</div>
            <h1>Turn trade promotions into structured retail execution.</h1>
            <p>
              Deploy trade promotion programs with stronger retailer participation, structured
              verification and real-time reporting — all within one connected platform built for
              real retail environments.
            </p>
            <p>
              Kartini enables brands to activate trade incentives, verify execution through Display
              IR and retailer workflows, and monitor program performance through one unified
              operating layer instead of fragmented manual processes.
            </p>
            <div className="hero-actions">
              <button className="btn dark" onClick={() => scrollToSection("cta")}>
                Launch a Trade Promotion Demo
              </button>
              <button className="btn" onClick={() => scrollToSection("flow")}>
                See How It Works
              </button>
            </div>
            <div className="stat-ribbon">
              <div className="stat-pill">
                <strong>Retail Participation</strong> Structured store engagement and incentive
                governance
              </div>
              <div className="stat-pill">
                <strong>Display IR</strong> Verification of display and shelf execution
              </div>
              <div className="stat-pill">
                <strong>Dompet Kartini</strong> Retailer wallet and reward visibility
              </div>
            </div>
          </div>
          <div className="hero-stage">
            <div className="device">
              <div className="device-top">
                <div className="device-title">
                  <div className="bubble">K</div>
                  <div>
                    <strong>Trade Program Console</strong>
                    <br />
                    <span style={{ color: "#666", fontSize: "13px" }}>
                      Retail execution and incentive flow
                    </span>
                  </div>
                </div>
                <div className="mini-pill">Display IR Active</div>
              </div>
              <div className="chat-flow">
                <div className="msg left">
                  Welcome. Your store has been enrolled in the current trade promotion program.
                </div>
                <div className="msg right">Show me today’s display task.</div>
                <div className="msg left">
                  Please upload your shelf display image and confirm product availability.
                </div>
                <div className="msg right">I’ve completed the upload.</div>
                <div className="msg left">
                  Great. Your execution is being reviewed and your reward status will update after
                  verification.
                </div>
              </div>
            </div>
            <div className="floating float-a">
              <strong>Retail Incentives</strong>
              <span>Program rules, target logic and reward qualification.</span>
            </div>
            <div className="floating float-b">
              <strong>Dompet Kartini</strong>
              <span>Retailer wallet for verification, uploads and incentive tracking.</span>
            </div>
            <div className="floating float-d">
              <strong>Display IR</strong>
              <span>Image Recognition validating display execution and compliance.</span>
            </div>
            {/* <div className="signal-line line-a"></div><div className="signal-line line-b"></div><div className="signal-line line-c"></div> */}
          </div>
        </section>

        <section className="wrap reveal" id="overview" ref={overviewRef}>
          <div className="head">
            <div>
              <div className="eyebrow">The Problem</div>
              <h2>Trade promotions should drive execution, not just incentives.</h2>
            </div>
            <p>
              Many trade promotion programs still depend on spreadsheets, disconnected field
              processes and delayed reporting. That makes it difficult for brands to know which
              retailers participated, which stores executed correctly and whether rewards were
              actually earned through valid in-store performance.
            </p>
          </div>
          <div className="grid4">
            <div className="panel metric-card">
              <div>
                <div className="metric-kicker">Challenge</div>
                <div className="metric-value">Manual retailer tracking</div>
              </div>
              <p>
                Participation is often captured through fragmented files and inconsistent field
                coordination.
              </p>
            </div>
            <div className="panel metric-card">
              <div>
                <div className="metric-kicker">Challenge</div>
                <div className="metric-value">Weak execution proof</div>
              </div>
              <p>
                Display claims and in-store compliance are difficult to verify at scale without
                structured validation.
              </p>
            </div>
            <div className="panel metric-card">
              <div>
                <div className="metric-kicker">Challenge</div>
                <div className="metric-value">Delayed incentive visibility</div>
              </div>
              <p>
                Retailers and brands lack clarity on reward status, qualification and reimbursement
                progress.
              </p>
            </div>
            <div className="panel metric-card">
              <div>
                <div className="metric-kicker">Challenge</div>
                <div className="metric-value">Poor field intelligence</div>
              </div>
              <p>
                Brands struggle to connect incentive programs with measurable store-level
                performance outcomes.
              </p>
            </div>
          </div>
        </section>

        <section className="wrap section-loose reveal">
          <div className="head">
            <div>
              <div className="eyebrow">What Kartini Enables</div>
              <h2>A unified trade promotion infrastructure.</h2>
            </div>
            <p>
              Kartini gives brands one platform to manage trade promotion mechanics, retailer
              participation, execution verification, reward governance and reporting without relying
              on disconnected retail processes.
            </p>
          </div>
          <div className="grid4">
            <div className="panel feature">
              <div className="icon">RP</div>
              <div>
                <h3>Retailer Participation</h3>
                <p>
                  Enroll and guide stores through trade programs with clearer participation logic
                  and communication flows.
                </p>
              </div>
              <div className="placeholder">Retailer onboarding interface</div>
            </div>
            <div className="panel feature">
              <div className="icon">EV</div>
              <div>
                <h3>Execution Verification</h3>
                <p>
                  Validate display compliance and in-store activity through Display IR and
                  structured submission workflows.
                </p>
              </div>
              <div className="placeholder">Display validation output</div>
            </div>
            <div className="panel feature">
              <div className="icon">RG</div>
              <div>
                <h3>Reward Governance</h3>
                <p>
                  Track qualification, incentive logic and reward status in a more transparent and
                  controlled environment.
                </p>
              </div>
              <div className="placeholder">Reward status view</div>
            </div>
            <div className="panel feature">
              <div className="icon">AN</div>
              <div>
                <h3>Analytics</h3>
                <p>
                  Monitor store participation, compliance and trade promotion performance with
                  real-time reporting.
                </p>
              </div>
              <div className="placeholder">Trade analytics dashboard</div>
            </div>
          </div>
        </section>

        <section className="wrap reveal" id="mechanics" ref={mechanicsRef}>
          <div className="head">
            <div>
              <div className="eyebrow">Key Promotion Mechanics</div>
              <h2>Built to support real retailer programs and field execution.</h2>
            </div>
            <p>
              Kartini supports different trade structures depending on campaign objectives, from
              display compliance to incentive-based retail participation and broader distribution
              programs.
            </p>
          </div>
          <div className="grid2">
            <div className="panel">
              <h3>Visibility & Distribution Drive</h3>
              <p>
                Reward stores for maintaining display execution, visibility compliance and product
                availability in line with program requirements.
              </p>
            </div>
            <div className="panel">
              <h3>Retail Incentive Programs</h3>
              <p>
                Provide structured rewards to stores or field teams based on verified retail
                outcomes and performance milestones.
              </p>
            </div>
            <div className="panel">
              <h3>Perfect Store Programs</h3>
              <p>
                Encourage planogram compliance and stronger merchandising standards across store
                environments.
              </p>
            </div>
            <div className="panel">
              <h3>Retail Activation Campaigns</h3>
              <p>
                Deploy store-level trade mechanics with clearer validation, reporting and incentive
                visibility.
              </p>
            </div>
          </div>
        </section>

        <section className="wrap section-loose reveal" id="flow" ref={flowRef}>
          <div className="head">
            <div>
              <div className="eyebrow">How It Works</div>
              <h2>
                A retail journey built for participation, verification and measurable execution.
              </h2>
            </div>
            <p>
              From program setup to reward release, Kartini structures the full trade promotion
              journey so brands can manage store participation without losing control of execution
              quality or reporting visibility.
            </p>
          </div>
          <div className="steps">
            <div className="step">
              <div className="step-num">1</div>
              <h3>Program Setup</h3>
              <p>Define trade mechanics, eligible stores, targets, products and reward logic.</p>
            </div>
            <div className="step">
              <div className="step-num">2</div>
              <h3>Retailer Participation</h3>
              <p>
                Retailers or field teams are guided into the program with structured instructions
                and submission logic.
              </p>
            </div>
            <div className="step">
              <div className="step-num">3</div>
              <h3>Execution Upload</h3>
              <p>
                Stores upload display images, proof of execution or required program evidence
                through Dompet Kartini.
              </p>
            </div>
            <div className="step">
              <div className="step-num">4</div>
              <h3>Verification</h3>
              <p>
                Display IR and program logic validate compliance, product presence and display
                quality.
              </p>
            </div>
            <div className="step">
              <div className="step-num">5</div>
              <h3>Reward Distribution</h3>
              <p>
                Qualified stores receive incentives or compensation based on verified trade
                performance.
              </p>
            </div>
          </div>
        </section>

        <section className="wrap reveal">
          <div className="head">
            <div>
              <div className="eyebrow">Why Structured Trade Systems Matter</div>
              <h2>Retail programs should be measurable from store action to incentive outcome.</h2>
            </div>
            <p>
              Trade promotions are most effective when store execution, reward logic and reporting
              live in one environment. That reduces friction for retailers and gives brands clearer
              visibility into how incentives translate into real execution in the market.
            </p>
          </div>
          <div className="channel-tiles">
            <div className="tile">
              <h3>Clearer Participation</h3>
              <p>Retailers know what to do, when to submit and how their rewards are determined.</p>
            </div>
            <div className="tile">
              <h3>Stronger Verification</h3>
              <p>
                Brands gain proof that store execution happened according to program expectations.
              </p>
            </div>
            <div className="tile">
              <h3>Better Reporting</h3>
              <p>
                Campaign visibility improves when retailer submissions, compliance and incentives
                are connected.
              </p>
            </div>
          </div>
        </section>

        <section className="wrap section-loose reveal">
          <div className="head">
            <div>
              <div className="eyebrow">Built for Retail Scale</div>
              <h2>
                Trade promotion infrastructure designed for real-world distribution complexity.
              </h2>
            </div>
            <p>
              Kartini supports large-scale retail environments, helping brands run trade initiatives
              across stores and field teams while keeping program governance and reporting
              structured.
            </p>
          </div>
          <div className="grid3">
            <div className="panel metric-card">
              <div>
                <div className="metric-kicker">Scale</div>
                <div className="metric-value">Multi-store program deployment</div>
              </div>
              <p>
                Run trade incentives across many locations without losing visibility on store
                activity.
              </p>
            </div>
            <div className="panel metric-card">
              <div>
                <div className="metric-kicker">Control</div>
                <div className="metric-value">Verified execution</div>
              </div>
              <p>
                Connect reward eligibility to actual display and retail performance instead of
                unverified claims.
              </p>
            </div>
            <div className="panel metric-card">
              <div>
                <div className="metric-kicker">Visibility</div>
                <div className="metric-value">Reward and compliance reporting</div>
              </div>
              <p>
                Monitor retailer participation, display quality and incentive outcomes in one
                operating layer.
              </p>
            </div>
          </div>
        </section>

        <section className="wrap reveal" id="use-cases" ref={useCasesRef}>
          <div className="head">
            <div>
              <div className="eyebrow">Use Cases</div>
              <h2>Trade promotion formats that support stronger field execution.</h2>
            </div>
            <p>
              Kartini is built for trade programs that require both incentive logic and proof of
              in-store execution.
            </p>
          </div>
          <div className="grid2">
            <div className="panel">
              <h3>Display Compliance Programs</h3>
              <p>
                Reward stores for meeting shelf display and merchandising requirements during
                campaigns.
              </p>
            </div>
            <div className="panel">
              <h3>Distribution Expansion Programs</h3>
              <p>
                Encourage stores to improve product availability and category presence within target
                locations.
              </p>
            </div>
            <div className="panel">
              <h3>Retailer Compensation Programs</h3>
              <p>
                Provide clear compensation flows tied to validated retail participation and
                execution.
              </p>
            </div>
            <div className="panel">
              <h3>Field Team Execution Campaigns</h3>
              <p>
                Support sales or merchandising teams with more measurable compliance and reporting
                structures.
              </p>
            </div>
          </div>
        </section>

        <section className="wrap section-loose reveal">
          <div className="head">
            <div>
              <div className="eyebrow">Connected Platform</div>
              <h2>Trade Promotions work stronger when connected to the wider Kartini ecosystem.</h2>
            </div>
            <p>
              Kartini trade programs become more powerful when linked with Display IR, Dompet
              Kartini and Analytics — creating a complete view of retailer participation, store
              execution and campaign performance.
            </p>
          </div>
          <div className="grid3">
            <div className="panel">
              <h3>Display IR</h3>
              <p>
                Validate shelf displays and visual execution automatically using Image Recognition.
              </p>
            </div>
            <div className="panel">
              <h3>Dompet Kartini</h3>
              <p>
                Enable retailer-facing submissions, verification and incentive visibility in one
                wallet environment.
              </p>
            </div>
            <div className="panel">
              <h3>Analytics</h3>
              <p>
                Track store participation, compliance outcomes and reward distribution in real time.
              </p>
            </div>
          </div>
        </section>

        <section className="wrap reveal" id="cta" ref={ctaRef}>
          <div className="cta-display">
            <div className="eyebrow">Get Started</div>
            <h2>Build trade promotions with full retail visibility.</h2>
            <p>
              Modern trade programs should not only distribute incentives — they should generate
              structured intelligence about retailer participation, display execution and field
              performance. Kartini enables brands to deploy trade promotions that are measurable,
              verifiable and scalable across real retail environments.
            </p>
            <div className="hero-actions" style={{ justifyContent: "center" }}>
              <button className="btn dark">Book a Trade Promotion Demo</button>
              <button className="btn">Explore the Kartini Platform</button>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default TradePromo;
